import { Box, Drawer, Grid, Skeleton, Typography } from '@mui/material';
import { useLocation, useNavigate } from '@tanstack/react-router';
import Logo from 'assets/icons/logo.svg?react';
import { Fonts, RoutePaths } from 'common-types';
import { useGetUserData } from 'hooks/use-get-user-data';
import { memo, useCallback } from 'react';
import { useStore } from 'store';
import { useShallow } from 'zustand/shallow';

import DefaultAvatar from './assets/default-avatar.png';
import EnlargeIcon from './assets/enlarge-icon.svg?react';
import EnlargeIconSecond from './assets/enlarge-icon-2.svg?react';
import { LinksList } from './components/links-list';
import { PremiumBadge } from './components/premium-badge';
import { avatarStyles, drawerStyles, profileBlockStyles } from './styles';

export const DrawerMenu = memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isSuccess: isUserGetUserDataSuccess,
    userName,
    userEmail,
    userPhoto,
    isSubscriptionExpired,
  } = useGetUserData();

  const { isDrawerDialogOpen, photo } = useStore(
    useShallow((state) => ({
      isDrawerDialogOpen: state.isDrawerDialogOpen,
      photo: state.photo,
    })),
  );

  const handleOpenDialog = (): void => {
    useStore.getState().setIsDrawerDialogOpen(true);
  };

  const handleRenewSubscriptionClick = useCallback(async (): Promise<void> => {
    navigate({ to: RoutePaths.RENEW_SUBSCRIPTION });
  }, [navigate]);

  return (
    <Drawer variant="permanent" sx={drawerStyles}>
      <Grid container flexWrap="nowrap" direction="column" component="aside" height="100%">
        <Grid container justifyContent="center" sx={{ p: 8 }}>
          <Logo />
        </Grid>
        <Grid>
          <LinksList />
        </Grid>
        {location.pathname !== RoutePaths.RENEW_SUBSCRIPTION && (
          <Grid sx={{ px: 6, my: 'auto', py: 4 }}>
            <>{isSubscriptionExpired && <PremiumBadge handleClick={handleRenewSubscriptionClick} />}</>
          </Grid>
        )}
        <Grid
          sx={[
            profileBlockStyles,
            {
              mt: 'auto',
              position: 'relative',
              p: 4,
              backgroundColor: isDrawerDialogOpen ? 'primary.light' : 'common.white',
            },
          ]}
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            width="100%"
            component="button"
            sx={{ cursor: 'pointer', opacity: 0 }}
            onClick={handleOpenDialog}
          />
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid>
              <Grid container>
                <Grid>
                  {isUserGetUserDataSuccess ? (
                    <Box component="img" src={photo || userPhoto || DefaultAvatar} alt="avatar" sx={avatarStyles} />
                  ) : (
                    <Skeleton variant="circular" width={36} height={36} />
                  )}
                </Grid>
                <Grid sx={{ ml: 1.5 }}>
                  {isUserGetUserDataSuccess ? (
                    <Typography
                      variant="body1"
                      noWrap
                      width={90}
                      fontFamily={`${Fonts.TITLE}, sans-serif`}
                      fontWeight={700}
                    >
                      {userName || 'Dear'}
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={100} height={18} />
                  )}
                  {isUserGetUserDataSuccess ? (
                    <Typography
                      variant="body2"
                      fontFamily={`${Fonts.TITLE}, sans-serif`}
                      width={90}
                      noWrap
                      color="custom.textGrey"
                    >
                      {userEmail}
                    </Typography>
                  ) : (
                    <Skeleton variant="rounded" width={90} height={10} />
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid sx={{ position: 'relative', width: 16, height: 16, pointerEvents: 'none' }}>
              <Box position="absolute">{isDrawerDialogOpen ? <EnlargeIconSecond /> : <EnlargeIcon />}</Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Drawer>
  );
});
